/**
 * One of the store's materials, as `GET /template-editor/materials` lists it.
 *
 * The same projection the `catalog.materials` binding resolves into a
 * section's `resolvedData` (backend `resolveMaterials`), so a material picked
 * in the editor can stand in for the resolved row until the next read.
 */
export interface EditorMaterial {
  id: string;
  name: string;
  slug: string;
  image: string | null;
  description: string | null;
  /** Descriptor labels; their index is the number the block prints. */
  features: string[];
}

function rowsOf(raw: unknown): unknown[] | null {
  if (Array.isArray(raw)) return raw;
  if (raw === null || typeof raw !== "object") return null;
  const data = (raw as { data?: unknown }).data;
  if (Array.isArray(data)) return data;
  // The merchant list's paginated envelope, in case the route reuses it.
  const docs = (data as { docs?: unknown } | null | undefined)?.docs;
  return Array.isArray(docs) ? docs : null;
}

const text = (v: unknown): string => (typeof v === "string" ? v : "");

/**
 * The response, read into materials. `null` when it is not a list at all:
 * "no materials" is something to tell a merchant, so it is only ever said
 * about a list that came back empty.
 */
export function readEditorMaterials(raw: unknown): EditorMaterial[] | null {
  const rows = rowsOf(raw);
  if (!rows) return null;
  const out: EditorMaterial[] = [];
  for (const row of rows) {
    if (row === null || typeof row !== "object") continue;
    const r = row as Record<string, unknown>;
    const id = text(r.id);
    if (!id) continue;
    out.push({
      id,
      name: text(r.name),
      slug: text(r.slug),
      image: text(r.image) || null,
      description: text(r.description) || null,
      features: Array.isArray(r.features)
        ? r.features.filter((f): f is string => typeof f === "string")
        : [],
    });
  }
  return out;
}
